import React from "react";
import Select, { SingleValue, MultiValue, ActionMeta } from "react-select";

export interface OptionType {
  label: string;
  value: string | number;
  [key: string]: any;
}

interface CustomSelectProps {
  options: OptionType[];
  value?: OptionType | OptionType[] | null;
  onChange?: (
    value: SingleValue<OptionType> | MultiValue<OptionType>,
    action: ActionMeta<OptionType>
  ) => void;
  isMulti?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  isSearchable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  loadingMessage?: () => string;
  className?: string;
  classNamePrefix?: string;
  [key: string]: any;
}

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: "0.75rem",
    borderColor: state.isFocused ? "#16a34a" : "#e5e7eb",
    borderWidth: "2px",
    padding: "4px 8px",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(22, 163, 74, 0.1)" : "none",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#16a34a" },
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: "0.5rem",
    backgroundColor: state.isSelected
      ? "#16a34a"
      : state.isFocused
      ? "#dcfce7"
      : "transparent",
    fontSize: "14px",
    color: state.isSelected ? "white" : "#1f2937",
    padding: "10px 14px",
    cursor: "pointer",
    margin: "2px 6px",
    transition: "all 0.15s ease",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "14px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "15px",
    fontWeight: 500,
    color: "#111827",
  }),
  multiValue: (provided) => ({
    ...provided,
    borderRadius: "0.5rem",
    backgroundColor: "#dcfce7",
    fontSize: "14px",
    padding: "2px 4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#166534",
    fontWeight: 500,
    paddingLeft: "8px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#16a34a",
    borderRadius: "0.5rem",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#bbf7d0",
      color: "#14532d",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.75rem",
    overflow: "hidden",
    boxShadow:
      "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  }),
};

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  isMulti = false,
  placeholder = "Select...",
  isClearable = false,
  isSearchable = true,
  isDisabled = false,
  isLoading = false,
  loadingMessage = () => "Loading...",
  className = "",
  classNamePrefix = "select",
  ...props
}) => {
  return (
    <Select
      options={options}
      value={value}
      onChange={onChange}
      isMulti={isMulti}
      placeholder={placeholder}
      isClearable={isClearable}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      className={className}
      classNamePrefix={classNamePrefix}
      styles={customSelectStyles}
      {...props}
    />
  );
};

export default CustomSelect;
